import { RealtimeServerEvent } from 'openai/resources/beta/realtime/realtime.mjs'
import { VoiceChatEventType, WebRTCEvent, WebRTCEventMap } from './types'

export class VoiceModeWebRTCClient {
  private peerConnection: RTCPeerConnection | null = null
  private dataChannel: RTCDataChannel | null = null
  private mediaStream: MediaStream | null = null
  private audioElement: HTMLAudioElement | null = null
  // Define type-safe event listeners with generic typing
  private eventListeners: Map<
    VoiceChatEventType,
    ((event: WebRTCEvent) => void)[]
  > = new Map()

  private processedEvents: Map<string, boolean> = new Map()

  constructor() {
    this.audioElement = document.createElement('audio')
    this.audioElement.autoplay = true
    this.audioElement.muted = false
    this.audioElement.volume = 1
  }

  async connect(rtcServer: string, rtcToken: string): Promise<boolean> {
    try {
      this.peerConnection = new RTCPeerConnection()

      await this.addAudioStream()

      this.peerConnection.addEventListener('track', (event) => {
        if (event.track.kind === 'audio' && this.audioElement) {
          this.audioElement.srcObject = event.streams[0]
        }
      })

      this.dataChannel = this.peerConnection.createDataChannel('oai-events', {
        ordered: true,
      })

      this.dataChannel.addEventListener('message', (event: MessageEvent) =>
        this.handleDataChannelMessage(event),
      )
      this.dataChannel.addEventListener('error', (error) =>
        this.handleDataChannelError(error),
      )

      const offer = await this.peerConnection.createOffer()
      await this.peerConnection.setLocalDescription(offer)

      await this.waitForIceCandidates()

      const response = await fetch(rtcServer, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/sdp',
          Authorization: `Bearer ${rtcToken}`,
        },
        body: offer.sdp!,
      })

      if (!response.ok) {
        throw new Error(
          `Failed to connect to OpenAI WebRTC: ${response.statusText}`,
        )
      }

      const sdpResponse = await response.text()

      await this.peerConnection.setRemoteDescription(
        new RTCSessionDescription({
          type: 'answer',
          sdp: sdpResponse,
        }),
      )

      console.log('WebRTC connection established successfully')
      return true
    } catch (error) {
      console.error('Error connecting to WebRTC server:', error)
      this.dispatchEvent({
        type: VoiceChatEventType.ERROR,
        error: 'Failed to establish WebRTC connection',
        details: error,
      })

      this.disconnect()
      return false
    }
  }

  async addAudioStream(): Promise<boolean> {
    if (!this.peerConnection) {
      console.error('Cannot add audio stream: peer connection not initialized')
      return false
    }

    try {
      console.log('Requesting microphone access')

      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      })
      console.log('Microphone access granted')

      const audioTrack = this.mediaStream.getTracks()[0]
      this.peerConnection.addTrack(audioTrack)

      return true
    } catch (error) {
      console.error('Error adding audio stream:', error)
      this.dispatchEvent({
        type: VoiceChatEventType.ERROR,
        error: 'Failed to access microphone',
        details: error,
      })
      return false
    }
  }

  private waitForIceCandidates(): Promise<void> {
    return new Promise((resolve) => {
      if (
        this.peerConnection?.iceGatheringState === 'complete' ||
        this.peerConnection?.iceConnectionState === 'connected'
      ) {
        resolve()
        return
      }

      const checkState = () => {
        if (this.peerConnection?.iceGatheringState === 'complete') {
          this.peerConnection.removeEventListener(
            'icegatheringstatechange',
            checkState,
          )
          resolve()
        }
      }

      this.peerConnection?.addEventListener(
        'icegatheringstatechange',
        checkState,
      )

      setTimeout(resolve, 5000)
    })
  }

  private handleDataChannelMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data) as RealtimeServerEvent

      // Idempotency, sometimes we receive the same event multiple times
      if (this.processedEvents.has(data.event_id)) {
        return
      }

      this.processedEvents.set(data.event_id, true)

      switch (data.type) {
        // NOTE: Before I was using input_audio_buffer.speech_started, but it sometimes
        // triggered multiple times, so I switched to this event instead.
        case 'input_audio_buffer.committed':
          this.dispatchEvent({
            type: VoiceChatEventType.SPEECH_CAPTURED,
            itemId: data.item_id,
          })
          break

        case 'conversation.item.input_audio_transcription.completed':
          this.dispatchEvent({
            type: VoiceChatEventType.USER_TRANSCRIPTION_COMPLETED,
            itemId: data.item_id,
            text: data.transcript,
          })
          break

        case 'response.content_part.added':
          this.dispatchEvent({
            type: VoiceChatEventType.ASSISTANT_RESPONSE_STARTED,
            itemId: data.response_id,
          })
          break

        case 'response.audio_transcript.delta':
          if (data.delta) {
            this.dispatchEvent({
              type: VoiceChatEventType.ASSISTANT_RESPONSE_DELTA,
              itemId: data.response_id,
              delta: data.delta as string,
            })
          }
          break

        case 'response.audio_transcript.done':
          this.dispatchEvent({
            type: VoiceChatEventType.ASSISTANT_RESPONSE_DONE,
            itemId: data.response_id,
            text: data.transcript,
          })
          break

        // @ts-ignore - Not present in the types
        case 'output_audio_buffer.started':
          this.dispatchEvent({
            type: VoiceChatEventType.SPEAKING_STARTED,
            // @ts-ignore - Not present in the types
            itemId: data.response_id,
          })
          break

        // @ts-ignore - Not present in the types
        case 'output_audio_buffer.cleared':
          this.dispatchEvent({
            type: VoiceChatEventType.SPEAKING_ENDED,
            // @ts-ignore - Not present in the types
            itemId: data.response_id,
          })
          break

        // @ts-ignore - Not present in the types
        case 'output_audio_buffer.stopped':
          this.dispatchEvent({
            type: VoiceChatEventType.SPEAKING_ENDED,
            // @ts-ignore - Not present in the types
            itemId: data.response_id,
          })
          break

        case 'response.done':
          this.dispatchEvent({
            type: VoiceChatEventType.COMPLETION_USAGE,
            usage: data.response.usage,
          })
          break
      }
    } catch (error) {
      console.error('Error parsing WebRTC message:', {
        error,
        data: event.data,
      })
    }
  }

  private handleDataChannelError(error: Event): void {
    this.dispatchEvent({
      type: VoiceChatEventType.ERROR,
      error: 'WebRTC data channel error',
      details: error,
    })
  }

  disconnect(): void {
    this.processedEvents.clear()

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop())
      this.mediaStream = null
    }

    if (this.dataChannel) {
      this.dataChannel.close()
      this.dataChannel = null
    }

    if (this.peerConnection) {
      this.peerConnection.close()
      this.peerConnection = null
    }

    console.log('WebRTC session fully disconnected')
  }

  // Type-safe addEventListener using the event map
  addEventListener<T extends VoiceChatEventType>(
    type: T,
    listener: (event: WebRTCEventMap[T]) => void,
  ): void {
    if (!this.eventListeners.has(type)) {
      this.eventListeners.set(type, [])
    }

    // Type assertion needed since we're storing a more specific type
    // in a more general array
    this.eventListeners
      .get(type)
      ?.push(listener as (event: WebRTCEvent) => void)
  }

  // Type-safe removeEventListener using the event map
  removeEventListener<T extends VoiceChatEventType>(
    type: T,
    listener: (event: WebRTCEventMap[T]) => void,
  ): void {
    const listeners = this.eventListeners.get(type)

    if (listeners) {
      // Type assertion needed for comparison
      const typedListener = listener as (event: WebRTCEvent) => void
      const index = listeners.indexOf(typedListener)

      if (index !== -1) {
        listeners.splice(index, 1)
      }
    }
  }

  private dispatchEvent(event: WebRTCEvent): void {
    const listeners = this.eventListeners.get(event.type)

    if (listeners) {
      listeners.forEach((listener) => listener(event))
    }
  }
}
